import { z } from 'zod';

import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { buildV3CommsReceipt } from '@apps/web/features/phoebe_chat_v3/model/comms_receipt';
import {
    optionalTrimmed,
    type V3CommsPresentation,
    type V3CommsProposal,
    type V3CommsRecipient,
} from '@apps/web/features/phoebe_chat_v3/model/comms_types';

const nonEmptyString = z.string().trim().min(1);

const recipientIdentitySchema = z
    .object({
        name: nonEmptyString,
        caregiver_id: nonEmptyString.optional(),
        conversation_id: nonEmptyString.optional(),
        content: z.string().optional(),
    })
    .refine(
        ({ caregiver_id, conversation_id }) =>
            (caregiver_id === undefined) !== (conversation_id === undefined),
    );

const sendSmsArgumentsSchema = z.object({
    recipients: z.array(recipientIdentitySchema).min(1),
    content: z.string().optional(),
    target_language: z.string().optional(),
    send_anyway: z.boolean().optional(),
});

const placeVoiceCallArgumentsSchema = z
    .object({
        instructions: nonEmptyString,
        name: nonEmptyString,
        caregiver_id: nonEmptyString.optional(),
        conversation_id: nonEmptyString.optional(),
        opening_line: z.string().optional(),
        send_anyway: z.boolean().optional(),
    })
    .refine(
        ({ caregiver_id, conversation_id }) =>
            (caregiver_id === undefined) !== (conversation_id === undefined),
    );

const approvePendingMessageArgumentsSchema = z.object({
    name: nonEmptyString,
    conversation_id: nonEmptyString,
    pending_message_id: nonEmptyString,
    draft_content: z.string().optional(),
    updated_content: z.string().optional(),
});

const editPendingMessageArgumentsSchema = z.object({
    conversation_id: nonEmptyString,
    pending_message_id: nonEmptyString,
    updated_content: nonEmptyString,
});

const rejectPendingMessageArgumentsSchema = z.object({
    conversation_id: nonEmptyString,
    pending_message_id: nonEmptyString,
    reason: z.string().optional(),
});

function parseSendSmsProposal(rawArguments: unknown): V3CommsProposal | null {
    const parsed = sendSmsArgumentsSchema.safeParse(rawArguments);
    if (!parsed.success) {
        return null;
    }
    const shared = optionalTrimmed(parsed.data.content);
    const recipients: Array<V3CommsRecipient> = [];
    for (const recipient of parsed.data.recipients) {
        // A recipient's own content wins; a blank override is malformed
        // rather than silently replaced by the shared wording.
        const content =
            optionalTrimmed(recipient.content) ??
            (recipient.content === undefined ? shared : null);
        if (content === null) {
            return null;
        }
        recipients.push({ name: recipient.name, content });
    }
    const allShared = recipients.every(
        (recipient) => recipient.content === recipients[0]?.content,
    );
    const targetLanguage = optionalTrimmed(
        parsed.data.target_language,
    )?.toLowerCase();
    return {
        tool: 'send_sms',
        recipients,
        sharedContent: allShared ? (recipients[0]?.content ?? null) : null,
        targetLanguage:
            targetLanguage === undefined || targetLanguage === 'en'
                ? null
                : targetLanguage,
        sendAnyway: parsed.data.send_anyway === true,
    };
}

function parseProposal(
    toolName: string,
    rawArguments: unknown,
): V3CommsProposal | null {
    switch (toolName) {
        case 'send_sms':
            return parseSendSmsProposal(rawArguments);
        case 'place_voice_call': {
            const parsed =
                placeVoiceCallArgumentsSchema.safeParse(rawArguments);
            if (!parsed.success) {
                return null;
            }
            return {
                tool: 'place_voice_call',
                name: parsed.data.name,
                instructions: parsed.data.instructions,
                openingLine: optionalTrimmed(parsed.data.opening_line),
                sendAnyway: parsed.data.send_anyway === true,
            };
        }
        case 'approve_pending_message': {
            const parsed =
                approvePendingMessageArgumentsSchema.safeParse(rawArguments);
            if (!parsed.success) {
                return null;
            }
            const rewrite = optionalTrimmed(parsed.data.updated_content);
            const content =
                rewrite ?? optionalTrimmed(parsed.data.draft_content);
            if (content === null) {
                return null;
            }
            return {
                tool: 'approve_pending_message',
                name: parsed.data.name,
                content,
                isRewrite: rewrite !== null,
            };
        }
        case 'edit_pending_message': {
            const parsed =
                editPendingMessageArgumentsSchema.safeParse(rawArguments);
            if (!parsed.success) {
                return null;
            }
            return {
                tool: 'edit_pending_message',
                content: parsed.data.updated_content,
            };
        }
        case 'reject_pending_message': {
            const parsed =
                rejectPendingMessageArgumentsSchema.safeParse(rawArguments);
            if (!parsed.success) {
                return null;
            }
            return {
                tool: 'reject_pending_message',
                reason: optionalTrimmed(parsed.data.reason),
            };
        }
        default:
            return null;
    }
}

/**
 * Build the native communications presentation for a tool entry, or null
 * when the tool is not a communications tool or its arguments do not match
 * the tool's contract — the caller then keeps the generic tool row.
 *
 * `status` is the adapter's normalized status ('complete'/'running' when the
 * entry carries none of its own).
 */
export function parseV3CommsPresentation(
    entry: ToolExecutionEntry,
    status: string,
): V3CommsPresentation | null {
    const proposal = parseProposal(entry.name, entry.arguments);
    if (proposal === null) {
        return null;
    }
    // A denied approval never runs the tool, so it settles without output.
    const denied = entry.approvalState === 'denied';
    if (denied || entry.isComplete) {
        return {
            kind: 'comms',
            proposal,
            phase: 'settled',
            receipt: buildV3CommsReceipt({
                proposal,
                output: entry.output,
                status,
                denied,
                hasError: Boolean(entry.error),
            }),
        };
    }
    if (
        entry.approvalState === 'pending' ||
        entry.status === 'pending_approval'
    ) {
        return {
            kind: 'comms',
            proposal,
            phase: 'needs_review',
            receipt: null,
        };
    }
    return { kind: 'comms', proposal, phase: 'working', receipt: null };
}
